import Box from '@mui/material/Box';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { AnimatedEllipsis } from 'src/components/common/Loading/AnimatedEllipsis';
import { StacklineLogoBlueIcon } from '../SvgIcons/SvgIcons';
import AuthButton from './AuthButton';
import AuthContainer from './AuthContainer';
import { Text } from 'src/components/BeaconV2Components/Generic/Text';

import LogoContainer from './LogoContainer';
import SignInInputField from './SignInInputField';
import ReduxStore from 'src/types/store/reduxStore';
import { useStacklineTheme } from '@stackline/ui';
import { clearMessage } from 'src/store/modules/auth/operations';
import { getAppDisplayName, getAppName } from 'src/utils/app';
import { getLoginContainerConfig } from 'src/components/SignIn/SignInContainer';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';

interface SignInProps {
  app: ReduxStore['app'];
  handleChange: (value: { email?: string; password?: string; buttonText?: string; authProvider?: string }) => void;
  handleFormSubmit: (event: React.FormEvent<HTMLFormElement>) => void;
  loading: boolean;
  email: string;
  authProvider: string | null;
  handleEmailChange: (newEmail: string) => void;
  buttonText?: string;
}

const SignIn = ({ handleChange, handleFormSubmit, authProvider, loading, email, handleEmailChange }: SignInProps) => {
  const appName = getAppName();
  const title = getAppDisplayName(appName);
  const textColor = getLoginContainerConfig(appName).textColor;
  const theme = useStacklineTheme();

  const [password, setPassword] = useState('');
  const dispatch = useDispatch();

  // Update document title to match current app branding
  useEffect(() => {
    document.title = `Stackline | ${title}`;
  }, [title]);

  useEffect(() => {
    handleChange({ email, password });
  }, [email, handleChange, password]);

  const handleEmailInputChange = (e) => {
    // We want to close error notification when user type email or password
    dispatch(clearMessage());
    handleEmailChange(e.target.value);
  };

  const handlePasswordChange = (e) => {
    // We want to close error notification when user type email or password
    dispatch(clearMessage());
    setPassword(e.target.value);
  };

  return (
    <AuthContainer>
      <form onSubmit={handleFormSubmit} style={{ width: '100%' }}>
        <Flex flexDirection="column" gap={40}>
          <LogoContainer title={title} />

          <Flex flexDirection="column" gap={24}>
            <SignInInputField
              id="signin-username"
              label="Email"
              value={email}
              onChange={handleEmailInputChange}
              autoComplete="email"
              type="text"
            />
            {authProvider === 'cognito' && (
              <SignInInputField
                showForgotPassword
                value={password}
                id="signin-password"
                onChange={handlePasswordChange}
                label="Password"
                type="password"
                autoComplete="current-password"
              />
            )}
          </Flex>

          <AuthButton disabled={loading} type="submit">
            {loading ? (
              <Box
                display="flex"
                flexDirection="row"
                maxHeight="100%"
                alignItems="center"
                justifyContent="center"
                fontWeight="600"
                fontFamily="Geist"
              >
                Loading
                <AnimatedEllipsis width={40} height={40} style={{ margin: 0, transform: 'translate(-8px,5px)' }} />
              </Box>
            ) : (
              'Continue'
            )}
          </AuthButton>
        </Flex>
        {/* <AuthTitle marginTop="24px" marginBottom={theme.spacing.lg}>
          Hey, good to see you again!
        </AuthTitle> */}

        <Box mt={theme.spacing.xxl} gap="12px" display="flex" flexDirection="column" alignItems="center">
          <StacklineLogoBlueIcon width="97.1" height="17" style={{ display: 'block' }} />
          <Text variant="body2" sx={{ fontFamily: 'Geist', fontWeight: 500 }}>
            Don’t have an account?{' '}
            <a style={{ color: textColor }} href="https://www.stackline.com/contact">
              {/* This should remain an anchor tag since we are linking outside the web app */}
              Contact us
            </a>
          </Text>
        </Box>
      </form>
    </AuthContainer>
  );
};

export default SignIn;
